import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import { User, Mail, Lock, Calendar, HelpCircle, Eye, EyeOff, Check, X, BookOpen } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import { API } from '../utils/api';

export default function Register() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [secretQuestions, setSecretQuestions] = useState([]);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [emailAvailable, setEmailAvailable] = useState(null);
  
  const [formData, setFormData] = useState({
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
    name: "",
    date_of_birth: "",
    secret_question: "",
    secret_answer: "",
  });

  // Password strength indicators
  const passwordChecks = {
    length: formData.password.length >= 8,
    uppercase: /[A-Z]/.test(formData.password),
    lowercase: /[a-z]/.test(formData.password),
    number: /\d/.test(formData.password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password),
  };
  
  const passwordStrong = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = formData.password === formData.confirmPassword && formData.confirmPassword.length > 0;

  useEffect(() => {
    fetchSecretQuestions();
  }, []);

  // Debounced username check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.username.length >= 3) {
        checkUsername(formData.username);
      } else {
        setUsernameAvailable(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.username]);

  // Debounced email check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.email && formData.email.includes("@")) {
        checkEmail(formData.email);
      } else {
        setEmailAvailable(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.email]);

  const fetchSecretQuestions = async () => {
    try {
      const response = await fetch(`${API}/auth/secret-questions`);
      const data = await response.json();
      setSecretQuestions(data.questions || []);
    } catch (error) {
      console.error("Error fetching secret questions:", error);
    }
  };

  const checkUsername = async (username) => {
    try {
      const response = await fetch(`${API}/auth/check-username/${username}`);
      const data = await response.json();
      setUsernameAvailable(data.available);
    } catch (error) {
      console.error("Error checking username:", error);
    }
  };

  const checkEmail = async (email) => {
    try {
      const response = await fetch(`${API}/auth/check-email/${encodeURIComponent(email)}`);
      const data = await response.json();
      setEmailAvailable(data.available);
    } catch (error) {
      console.error("Error checking email:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validations
    if (!passwordStrong) {
      toast.error("Please ensure your password meets all requirements");
      return;
    }
    
    if (!passwordsMatch) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (!formData.secret_question) {
      toast.error("Please select a secret question");
      return;
    }
    
    if (formData.secret_answer.length < 2) {
      toast.error("Secret answer must be at least 2 characters");
      return;
    }

    setLoading(true);

    try {
      // Use axios instead of fetch for better error handling
      await axios.post(`${API}/auth/register`, {
        email: formData.email,
        username: formData.username,
        password: formData.password,
        name: formData.name,
        date_of_birth: formData.date_of_birth,
        secret_question: formData.secret_question,
        secret_answer: formData.secret_answer,
      });
      
      toast.success("Registration successful! Welcome to LifeLedger!");
      
      // Fetch full user data and navigate with it
      try {
        const meResponse = await axios.get(`${API}/auth/me`);
        navigate("/dashboard", { state: { user: meResponse.data }, replace: true });
      } catch (meError) {
        // Fallback: navigate without user data
        navigate("/dashboard", { replace: true });
      }
    } catch (error) {
      console.error("Registration error:", error);
      const message = error.response?.data?.detail || error.message || "Registration failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-black" />
          </div>
          <span className="text-2xl font-bold text-white">LifeLedger</span>
        </div>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-2xl text-white text-center">Create Account</CardTitle>
            <p className="text-gray-400 text-center text-sm">
              Join LifeLedger to manage your finances
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <Label className="text-gray-300">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                    className="pl-10 bg-secondary border-border text-white placeholder:text-gray-500"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <Label className="text-gray-300">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com"
                    required
                    className="pl-10 bg-secondary border-border text-white placeholder:text-gray-500"
                  />
                  {emailAvailable !== null && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      {emailAvailable ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : (
                        <X className="w-4 h-4 text-red-400" />
                      )}
                    </div>
                  )}
                </div>
                {emailAvailable === false && (
                  <p className="text-red-400 text-xs mt-1">Email already registered</p>
                )}
              </div>

              {/* Username */}
              <div>
                <Label className="text-gray-300">Username</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase() })}
                    placeholder="johndoe"
                    required
                    minLength={3}
                    maxLength={20}
                    pattern="[a-zA-Z0-9_]+"
                    className="pl-10 bg-secondary border-border text-white placeholder:text-gray-500"
                  />
                  {usernameAvailable !== null && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      {usernameAvailable ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : (
                        <X className="w-4 h-4 text-red-400" />
                      )}
                    </div>
                  )}
                </div>
                <p className="text-gray-500 text-xs mt-1">3-20 characters, letters, numbers, and underscores only</p>
                {usernameAvailable === false && (
                  <p className="text-red-400 text-xs">Username already taken</p>
                )}
              </div>

              {/* Password */}
              <div>
                <Label className="text-gray-300">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    className="pl-10 pr-10 bg-secondary border-border text-white placeholder:text-gray-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {/* Password strength indicators */}
                <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                  <div className={`flex items-center gap-1 ${passwordChecks.length ? 'text-green-400' : 'text-gray-500'}`}>
                    {passwordChecks.length ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    8+ characters
                  </div>
                  <div className={`flex items-center gap-1 ${passwordChecks.uppercase ? 'text-green-400' : 'text-gray-500'}`}>
                    {passwordChecks.uppercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    Uppercase letter
                  </div>
                  <div className={`flex items-center gap-1 ${passwordChecks.lowercase ? 'text-green-400' : 'text-gray-500'}`}>
                    {passwordChecks.lowercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    Lowercase letter
                  </div>
                  <div className={`flex items-center gap-1 ${passwordChecks.number ? 'text-green-400' : 'text-gray-500'}`}>
                    {passwordChecks.number ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    Number
                  </div>
                  <div className={`flex items-center gap-1 ${passwordChecks.special ? 'text-green-400' : 'text-gray-500'}`}>
                    {passwordChecks.special ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    Special character
                  </div>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <Label className="text-gray-300">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="••••••••"
                    required
                    className="pl-10 bg-secondary border-border text-white placeholder:text-gray-500"
                  />
                  {formData.confirmPassword && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      {passwordsMatch ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : (
                        <X className="w-4 h-4 text-red-400" />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Date of Birth */}
              <div>
                <Label className="text-gray-300">Date of Birth</Label>
                <div className="relative mt-1">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    required
                    className="pl-10 bg-secondary border-border text-white"
                  />
                </div>
                <p className="text-gray-500 text-xs mt-1">Used for account recovery</p>
              </div>

              {/* Secret Question */}
              <div>
                <Label className="text-gray-300">Secret Question</Label>
                <Select
                  value={formData.secret_question}
                  onValueChange={(value) => setFormData({ ...formData, secret_question: value })}
                >
                  <SelectTrigger className="mt-1 bg-secondary border-border text-white">
                    <SelectValue placeholder="Select a security question" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {secretQuestions.map((question, index) => (
                      <SelectItem key={index} value={question} className="text-gray-300 focus:text-white focus:bg-secondary">
                        {question}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-gray-500 text-xs mt-1">Used for password recovery</p>
              </div>

              {/* Secret Answer */}
              <div>
                <Label className="text-gray-300">Secret Answer</Label>
                <div className="relative mt-1">
                  <HelpCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    value={formData.secret_answer}
                    onChange={(e) => setFormData({ ...formData, secret_answer: e.target.value })}
                    placeholder="Your answer"
                    required
                    className="pl-10 bg-secondary border-border text-white placeholder:text-gray-500"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || !passwordStrong || !passwordsMatch || usernameAvailable === false || emailAvailable === false}
                className="w-full bg-green-500 hover:bg-green-400 text-black font-semibold rounded-full"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Create Account"
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-400 text-sm">
                Already have an account?{" "}
                <Link to="/login" className="text-green-400 hover:text-green-300">
                  Sign In
                </Link>
              </p>
              <p className="text-gray-500 text-xs mt-2">
                Or{" "}
                <Link to="/" className="text-green-400 hover:text-green-300">
                  sign in with Google
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
